/**
 * Referral Storage Utility
 *
 * Captures referral parameters (?ref=CODE&source=CHANNEL) from the URL
 * and persists them in sessionStorage so they survive the OAuth popup flow.
 *
 * Flow:
 *   URL ?ref=LAUNCH30&source=whatsapp
 *   → captureReferralFromURL() stores in sessionStorage
 *   → After signup, getReferralFromStorage() retrieves it
 *   → clearReferralFromStorage() cleans up after use
 */

const STORAGE_KEY = 'fitd_referral';
const MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24 hours
const MAX_CODE_LENGTH = 50;
const CODE_PATTERN = /^[a-zA-Z0-9_-]+$/;

export interface ReferralParams {
  code: string;
  source: string;
  capturedAt: number; // epoch ms
}

/**
 * Validates a referral code: non-empty, alphanumeric (with _ and -), max 50 chars.
 */
function isValidCode(code: string): boolean {
  return code.length > 0 && code.length <= MAX_CODE_LENGTH && CODE_PATTERN.test(code);
}

/**
 * Reads `?ref` and `?source` from the current URL and stores them in sessionStorage.
 * No-op if `ref` is absent or invalid.
 */
export function captureReferralFromURL(): void {
  try {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('ref');
    if (!code || !isValidCode(code)) return;

    const data: ReferralParams = {
      code,
      source: params.get('source') || 'direct',
      capturedAt: Date.now(),
    };

    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // sessionStorage may be unavailable (private browsing, etc.)
  }
}

/**
 * Returns the stored referral or null if absent / expired / invalid.
 */
export function getReferralFromStorage(): ReferralParams | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const data: ReferralParams = JSON.parse(raw);

    if (!data.code || !isValidCode(data.code)) return null;
    if (Date.now() - data.capturedAt > MAX_AGE_MS) return null;

    return data;
  } catch {
    return null;
  }
}

/**
 * Removes the referral data from sessionStorage.
 */
export function clearReferralFromStorage(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore
  }
}
